<template>
    <div class="default-mapping-cell" :alvqa="alvqa + '-cell-' + mappingType + '-' + language">
        <v-autocomplete
            :value="value"
            :items="autocompleteItems"
            :loading="loading"
            :disabled="loading"
            :placeholder="$t('lang_GENERIC_CAPTION_NOT_SET')"
            :no-data-text="loading ? $t('lang_GENERIC_CAPTION_LOADING') : $t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
            clearable
            dense
            hide-details
            outlined
            item-text="text"
            item-value="value"
            @change="onChange"
            :alvqa="alvqa + '-autocomplete'"
        >
            <!-- Selected value: show the same ALV Template chip when a system mapping is picked -->
            <template v-slot:selection="{ item }">
                <span class="selection-text">{{ item.text }}</span>
                <v-chip
                    v-if="item.shareable === 'yes'"
                    x-small
                    outlined
                    color="standardBrownIcon"
                    class="ml-2"
                >
                    {{ $t('lang_ACCOUNT_MAPPINGS_CAPTION_ALV_TEMPLATE') }}
                </v-chip>
            </template>

            <!-- Group headers (subheader/divider) come from autocompleteItems entries -->
            <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-if="item.header" dense>
                    <v-list-item-content class="pool-subheader">
                        <span>{{ item.header }}</span>
                    </v-list-item-content>
                </v-list-item>
                <v-divider v-else-if="item.divider" />
                <v-list-item v-else v-bind="attrs" v-on="on" :alvqa="alvqa + '-option-' + item.alvId">
                    <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.title && item.title !== item.text">
                            {{ item.title }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="item.shareable === 'yes'">
                        <v-chip x-small color="standardBrownIcon" outlined>
                            {{ $t('lang_ACCOUNT_MAPPINGS_CAPTION_ALV_TEMPLATE') }}
                        </v-chip>
                    </v-list-item-action>
                </v-list-item>
            </template>
        </v-autocomplete>

        <!-- Inline save status -->
        <div class="cell-status">
            <v-progress-circular
                v-if="status === 'saving'"
                size="16"
                width="2"
                indeterminate
                color="buttonStandard"
                :alvqa="alvqa + '-status-saving'"
            />
            <v-icon
                v-else-if="status === 'saved'"
                small
                color="success"
                :alvqa="alvqa + '-status-saved'"
            >check_circle</v-icon>
            <v-icon
                v-else-if="status === 'error'"
                small
                color="error"
                :alvqa="alvqa + '-status-error'"
            >error</v-icon>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DefaultMappingCell',
    props: {
        mappingType: { type: String, required: true },
        language:    { type: String, required: true },
        value:       { default: null }, // numeric mappingId or null
        alvqa:       { type: String, default: 'company-default-mappings' },
    },
    data() {
        return {
            status: 'idle', // idle | saving | saved | error
            debounceTimer: null,
            pendingMappingId: undefined,
            statusTimer: null,
        }
    },
    computed: {
        // Derive the pool key from the statement type so 6 publishing cells
        // (or 1 label cell) share a single pool entry.
        domain() {
            if (this.mappingType && this.mappingType.indexOf('label_statements') === 0) return 'label_statements'
            return 'publishing_statements'
        },
        // Mapping domain a template must carry to be eligible for this slot. The
        // payee sheet brings its own placeholder set, so its templates sit in
        // `publishing_payee_statement` while the other publishing sheets share
        // `publishing_statements`.
        expectedDomain() {
            if (this.domain === 'label_statements') return 'label_statements'
            if (this.mappingType === 'publishing_statements_payee') return 'publishing_payee_statement'
            return 'publishing_statements'
        },
        pool() {
            return this.$store.getters.getCompanyDefaultMappingsPools[this.domain]
        },
        loading() {
            return !this.pool || this.pool.loading
        },
        // Filter the pool down to this cell's (domain, statementType, language).
        // statementType falls back to "no filter" when BE rows don't expose it,
        // same behavior as the existing AzPersons mapping pickers.
        matchingMappings() {
            if (!this.pool || !Array.isArray(this.pool.all)) return []
            return this.pool.all.filter(m => {
                const langOk   = !this.language || !m.language || m.language === this.language
                const domainOk = m.domain === this.expectedDomain
                const typeOk   = !m.statementType || m.statementType === this.mappingType
                return langOk && domainOk && typeOk
            })
        },
        autocompleteItems() {
            const yours  = []
            const system = []
            this.matchingMappings.forEach(m => {
                if (m.shareable === 'yes') system.push(m)
                else yours.push(m)
            })
            const items = []
            if (yours.length) {
                items.push({ header: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_FILTER_OPTION_MAINTAINER_SELF') })
                yours.forEach(m => items.push(m))
            }
            if (system.length) {
                if (items.length) items.push({ divider: true })
                items.push({ header: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_ALV_TEMPLATE') })
                system.forEach(m => items.push(m))
            }
            return items
        },
    },
    methods: {
        onChange(newValue) {
            // null when cleared; v-autocomplete clearable sends null
            this.pendingMappingId = newValue == null ? null : newValue
            this.scheduleSave()
        },
        scheduleSave() {
            if (this.debounceTimer) clearTimeout(this.debounceTimer)
            this.debounceTimer = setTimeout(this.flushSave, 600)
        },
        async flushSave() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
                this.debounceTimer = null
            }
            if (this.pendingMappingId === undefined) return

            const mappingId = this.pendingMappingId
            this.pendingMappingId = undefined
            this.status = 'saving'

            const result = await this.$store.dispatch('saveCompanyDefaultMapping', {
                mappingType: this.mappingType,
                language:    this.language,
                mappingId:   mappingId,
            })

            if (result && result.ok) {
                this.status = 'saved'
                if (this.statusTimer) clearTimeout(this.statusTimer)
                this.statusTimer = setTimeout(() => { this.status = 'idle' }, 1500)
            } else {
                this.status = 'error'
            }
        },
    },
    created() {
        // Dispatch unconditionally — the store action dedupes both cache hits
        // and in-flight requests so the 5 sibling publishing cells trigger
        // a single network call.
        this.$store.dispatch('fetchCompanyDefaultMappingsDomainPool', {
            domain: this.domain,
        })
    },
    beforeDestroy() {
        // Flush any pending save synchronously-ish so closing the dialog
        // doesn't drop the last cell change.
        if (this.debounceTimer) this.flushSave()
        if (this.statusTimer) clearTimeout(this.statusTimer)
    },
}
</script>

<style scoped>
.default-mapping-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}
.default-mapping-cell .v-autocomplete {
    flex: 1 1 auto;
}
.cell-status {
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
}
.pool-subheader {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--v-standardBrownIcon-base, #8a6d3b);
}
.selection-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
